/**
 * 견적 상태 전이. 저장소는 여기서 정한 것만 쓰고 스스로 판단하지 않는다.
 *
 *   DRAFT ──발송──▶ SENT ──열람·수동──▶ NEGOTIATING
 *                    └──────────┬──────────┘
 *                               ├─▶ WON / LOST
 *                               └─▶ 새 DRAFT 버전(version+1, 이전 share 회수)
 *
 * 고객 단계는 여기가 아니라 사건 규칙(`modules/customer/stage-events.ts`)이 정한다.
 */

import { QUOTE_STATUS_LABEL, QuoteDecision, QuoteStatus } from "./types";

export const QUOTE_TRANSITIONS: Record<QuoteStatus, readonly QuoteStatus[]> = {
  DRAFT: ["SENT"],
  SENT: ["NEGOTIATING", "WON", "LOST"],
  NEGOTIATING: ["WON", "LOST"],
  WON: [],
  LOST: [],
};

export const canTransition = (from: QuoteStatus, to: QuoteStatus): boolean =>
  QUOTE_TRANSITIONS[from].includes(to);

// 사용자에게 나가는 문구라 상태는 한글 라벨로 적는다.
export const quoteTransitionMessage = (from: QuoteStatus, to: QuoteStatus): string =>
  `견적을 「${QUOTE_STATUS_LABEL[from]}」에서 「${QUOTE_STATUS_LABEL[to]}」 상태로 바꿀 수 없습니다`;

export type QuoteBlockedAction = "edit" | "send" | "negotiate" | "newVersion";

const QUOTE_BLOCKED_TAIL: Record<QuoteBlockedAction, string> = {
  edit: "견적은 고칠 수 없습니다 — 새 버전을 만드세요",
  send: "견적은 발송할 수 없습니다",
  negotiate: "견적은 조율 중으로 바꿀 수 없습니다",
  newVersion: "견적에서는 새 버전을 만들 수 없습니다",
};

// 인메모리·Supabase 두 저장소가 같이 쓰는 상태 막힘 문구.
export const quoteBlockedMessage = (status: QuoteStatus, action: QuoteBlockedAction): string =>
  `「${QUOTE_STATUS_LABEL[status]}」 ${QUOTE_BLOCKED_TAIL[action]}`;

export const assertTransition = (from: QuoteStatus, to: QuoteStatus): void => {
  if (!canTransition(from, to)) {
    throw new Error(quoteTransitionMessage(from, to));
  }
};

export const canSend = (status: QuoteStatus): boolean =>
  canTransition(status, "SENT");

export const canDecide = (status: QuoteStatus): boolean =>
  canTransition(status, "WON") || canTransition(status, "LOST");

/** 발송된 뒤에만 새 버전을 뜬다. 초안은 그냥 고치면 되고 끝난 건은 다시 열지 않는다. */
export const canCreateNewVersion = (status: QuoteStatus): boolean =>
  status === "SENT" || status === "NEGOTIATING";

/** 초안 편집 가능 여부. 발송 후에는 새 버전으로만 고친다. */
export const canEditDraft = (status: QuoteStatus): boolean =>
  status === "DRAFT";

export interface QuoteDecisionPatch {
  status: QuoteDecision;
  decided_at: string;
  lost_reason: string | null;
}

/** 수주·이탈 확정. 전이표에 없으면 던진다 — 저장소가 상태를 흔들지 못하게. */
export const applyQuoteDecision = (
  from: QuoteStatus,
  decision: QuoteDecision,
  at: string,
  lostReason?: string | null
): QuoteDecisionPatch => {
  assertTransition(from, decision);
  return {
    status: decision,
    decided_at: at,
    lost_reason: decision === "LOST" ? (lostReason ?? null) : null,
  };
};
